import classNames from 'classnames';
import { useState, useRef, useEffect, useCallback } from 'react';

import Icon, { IconName } from '@utilities/icon';

interface TabsLabelProps {
  label: string;
  iconName?: string;
  id: string;
  threshold?: number;
}

interface TabsProps {
  labels: TabsLabelProps[];
}

const DEFAULT_VISIBILITY_THRESHOLD = 0.1;

const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  if (!element) return null;

  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = getComputedStyle(parent);

    if (overflowY === 'auto' || overflowY === 'scroll') {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
};

const getVisibleRatio = (
  rect: DOMRect,
  anchorTop: number,
  viewportBottom: number,
) => {
  const visibleTop = Math.max(rect.top, anchorTop);
  const visibleBottom = Math.min(rect.bottom, viewportBottom);
  const visibleHeight = Math.max(0, visibleBottom - visibleTop);

  return rect.height > 0 ? visibleHeight / rect.height : 0;
};

const TabsScrollTo = ({ labels }: TabsProps) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const activeTabRef = useRef(0);
  const tabsWrapperRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScroll = useRef(false);
  const rafId = useRef<number | null>(null);

  const getAnchorTop = useCallback(() => {
    return tabsWrapperRef.current?.getBoundingClientRect().bottom ?? 108;
  }, []);

  const updateActiveTab = useCallback(() => {
    if (isProgrammaticScroll.current) return;

    const anchorTop = getAnchorTop();
    const scrollParent = getScrollParent(tabsWrapperRef.current);
    const viewportBottom =
      scrollParent?.getBoundingClientRect().bottom ?? window.innerHeight;

    const sectionEntries = labels
      .map((label, index) => {
        const element = document.getElementById(label.id);
        if (!element) return null;

        const rect = element.getBoundingClientRect();
        const threshold = label.threshold ?? DEFAULT_VISIBILITY_THRESHOLD;

        return {
          index,
          rect,
          threshold,
          ratio: getVisibleRatio(rect, anchorTop, viewportBottom),
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);

    if (sectionEntries.length === 0) return;

    const qualifyingSections = sectionEntries.filter(
      (entry) => entry.rect.top <= anchorTop && entry.ratio >= entry.threshold,
    );

    let nextActiveTab = activeTabRef.current;

    if (qualifyingSections.length > 0) {
      nextActiveTab = qualifyingSections[qualifyingSections.length - 1].index;
    }

    setActiveTab((current) => {
      if (current === nextActiveTab) return current;

      activeTabRef.current = nextActiveTab;
      return nextActiveTab;
    });
  }, [getAnchorTop, labels]);

  const handleTabClick = (index: number) => {
    isProgrammaticScroll.current = true;
    activeTabRef.current = index;
    setActiveTab(index);

    const target = document.getElementById(labels[index]?.id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 400);
  };

  useEffect(() => {
    activeTabRef.current = 0;
    setActiveTab(0);
  }, [labels]);

  useEffect(() => {
    const scrollParent = getScrollParent(tabsWrapperRef.current);

    const handleScroll = () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }

      rafId.current = requestAnimationFrame(() => {
        updateActiveTab();
        rafId.current = null;
      });
    };

    updateActiveTab();

    scrollParent?.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      scrollParent?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);

      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [labels, updateActiveTab]);

  return (
    <div
      ref={tabsWrapperRef}
      className="sticky top-[-1px] bg-surface-primary w-full py-1 z-50"
    >
      <div className="bg-surface-secondary p-1 mx-4 overflow-x-auto overflow-y-hidden rounded-full scrollbar-none">
        <ul
          className={classNames(
            'flex text-Label/xSmall font-medium text-center text-black',
          )}
        >
          {labels?.map((item, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li
              className={classNames(
                labels.length < 6 ? 'w-full' : 'flex-shrink-0',
              )}
              key={index}
            >
              <button
                className={classNames(
                  'inline-flex items-center justify-center p-1 text-nowrap gap-1 text-Label/Medium transition-all group',
                  activeTab === index
                    ? 'text-orange-400 shadow-fab bg-surface-primary rounded-full'
                    : 'text-black',
                  labels.length < 6 && 'w-full',
                )}
                onClick={() => handleTabClick(index)}
              >
                {item.iconName && (
                  <Icon
                    name={item.iconName as IconName}
                    width={16}
                    height={16}
                    fill={activeTab === index ? '#ff7140' : 'text-orange-400'}
                  />
                )}
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default TabsScrollTo;
